import { compareWarmth } from './enums.js';
/**
 * When a member and an investor last spoke, out of the two records of it:
 * Affinity's reading of the email and calendar history, and what somebody
 * filed a report about. The later wins, and the answer says which it was,
 * because "we have not spoken since March" reads differently when the machine
 * says it than when a colleague does.
 *
 * Both are compared as text, which puts them in the same order as in time. A
 * day and an instant on that same day tie in favour of the instant: `2026-09-20`
 * is a prefix of `2026-09-20T10:00:00.000Z` and so sorts below it, which is the
 * right way round — the timestamped record is the more precise account of a day
 * they both agree about.
 */
export function latestContact(affinityAt, reportedOn) {
    if (affinityAt === null) {
        return reportedOn === null
            ? { at: null, source: null }
            : { at: reportedOn, source: 'reported' };
    }
    if (reportedOn === null)
        return { at: affinityAt, source: 'affinity' };
    return reportedOn > affinityAt
        ? { at: reportedOn, source: 'reported' }
        : { at: affinityAt, source: 'affinity' };
}
/** "through Greylock", or "through their old role at Greylock" once it has ended. */
export function throughWords(through) {
    return through.ended
        ? `through their old role at ${through.fundName}`
        : `through ${through.fundName}`;
}
/**
 * The best way in first. Warmth leads because it is somebody's judgement and a
 * machine's number is not allowed to outrank it — and because a warmth nobody
 * judged is already derived from the strength in bands, so the number has
 * mostly been read into the judgement by the time it is compared.
 *
 * Within a warmth, somebody known at this fund comes before somebody known
 * through another, whatever the numbers say: the fund's best pairing should be
 * a relationship about the fund where one is as warm.
 *
 * Affinity's strength then breaks the ties, which is most of them, and the
 * most recent contact breaks what is left.
 *
 * A value nobody has measured sorts below one that has been, on both the
 * strength and the date: an absent signal is not a weak one, but the row that
 * carries evidence is the row to read first.
 */
function isCarried(edge) {
    return edge.through !== undefined && edge.through !== null;
}
export function compareWhoKnows(left, right) {
    return (compareWarmth(left.warmth, right.warmth) ||
        Number(isCarried(left)) - Number(isCarried(right)) ||
        (right.affinityStrength ?? -1) - (left.affinityStrength ?? -1) ||
        // ISO instants compare as text in the same order they compare as time, and
        // the empty string a missing date becomes sorts below every real one.
        (right.lastContactAt ?? '').localeCompare(left.lastContactAt ?? '') ||
        left.memberName.localeCompare(right.memberName) ||
        left.investorName.localeCompare(right.investorName) ||
        left.id.localeCompare(right.id));
}
//# sourceMappingURL=whoKnows.js.map